import React, { useState, useEffect } from 'react';
import campaignsAPI from '../api/campaigns.js';
import '../styles/CampaignDrawer.css';

const STEPS = ['Template', 'Subject', 'Contacts', 'Review & Send'];

function parseManualEmails(raw) {
  return [...new Set(
    raw.split(/[\n,;]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e.includes('@') && e.includes('.'))
  )];
}

export default function CampaignDrawer({ template, onClose, onSent }) {
  const [step, setStep] = useState(0);
  const [subject, setSubject] = useState(template?.name || '');
  const [contactSource, setContactSource] = useState(null);
  const [crmLists, setCrmLists] = useState([]);
  const [fansCount, setFansCount] = useState(null);
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [manualEmails, setManualEmails] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null); // { recipientCount }
  const [error, setError] = useState('');

  useEffect(() => {
    campaignsAPI.getCRMLists().then(r => setCrmLists(r.data || [])).catch(() => {});
    campaignsAPI.getCommunityFansCount().then(r => setFansCount(r.data?.count ?? null)).catch(() => {});
  }, []);

  const parsedManual = parseManualEmails(manualEmails);

  const recipientCount = () => {
    if (!contactSource) return 0;
    if (contactSource.type === 'community-fans') return fansCount || 0;
    if (contactSource.type === 'crm-list') {
      return crmLists.filter(l => selectedListIds.includes(l._id)).reduce((sum, l) => sum + (l.count || 0), 0);
    }
    if (contactSource.type === 'manual') return parsedManual.length;
    return 0;
  };

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      let cs;
      if (contactSource.type === 'crm-list') {
        cs = { type: 'crm-list', ids: selectedListIds, label: crmLists.filter(l => selectedListIds.includes(l._id)).map(l => l.name).join(', ') };
      } else if (contactSource.type === 'community-fans') {
        cs = { type: 'community-fans', ids: [], label: 'Community Fans' };
      } else if (contactSource.type === 'manual') {
        cs = { type: 'manual', emails: parsedManual, ids: [], label: `${parsedManual.length} manual email${parsedManual.length !== 1 ? 's' : ''}` };
      }

      const created = await campaignsAPI.create({
        name: `${template?.name} — ${new Date().toLocaleDateString()}`,
        templateId: template?._id,
        subject,
        contactSource: cs,
      });

      if (!created.success) throw new Error(created.error || 'Failed to create campaign');

      const sendResult = await campaignsAPI.send(created.data._id);
      const count = sendResult?.data?.recipientCount ?? recipientCount();
      setSent({ recipientCount: count, campaign: created.data });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Send failed');
      setSending(false);
    }
  };

  const canProceed = () => {
    if (step === 0) return !!template;
    if (step === 1) return subject.trim().length > 0;
    if (step === 2) {
      if (!contactSource) return false;
      if (contactSource.type === 'crm-list') return selectedListIds.length > 0;
      if (contactSource.type === 'manual') return parsedManual.length > 0;
      return true;
    }
    return true;
  };

  if (sent) {
    const handleDone = () => {
      onSent && onSent(sent.campaign);
      onClose();
    };
    return (
      <div className="campaign-drawer-overlay" onClick={(e) => e.target === e.currentTarget && handleDone()}>
        <div className="campaign-drawer">
          <div className="campaign-drawer-header">
            <h2>Campaign Sent</h2>
            <button className="drawer-close-btn" onClick={handleDone}>×</button>
          </div>
          <div className="campaign-drawer-body campaign-success-body">
            <div className="campaign-success-icon">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="28" r="28" fill="rgba(236,72,153,0.12)" />
                <circle cx="28" cy="28" r="20" fill="rgba(236,72,153,0.18)" />
                <path d="M18 28.5L24.5 35L38 21" stroke="#EC4899" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="campaign-success-title">Campaign sent</div>
            <div className="campaign-success-count">
              <span className="campaign-success-num">{sent.recipientCount}</span>
              <span className="campaign-success-label">email{sent.recipientCount !== 1 ? 's' : ''} delivered</span>
            </div>
            <div className="campaign-success-hint">Opens and clicks will appear in Analytics.</div>
          </div>
          <div className="campaign-drawer-footer">
            <button className="drawer-btn-send" onClick={handleDone}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-drawer-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="campaign-drawer">
        <div className="campaign-drawer-header">
          <h2>Send Campaign</h2>
          <button className="drawer-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="campaign-stepper">
          {STEPS.map((label, i) => (
            <div
              key={i}
              className={`stepper-step ${i === step ? 'active' : ''} ${i < step ? 'completed' : ''}`}
              onClick={() => i < step && setStep(i)}
            >
              {label}
            </div>
          ))}
        </div>

        <div className="campaign-drawer-body">
          {step === 0 && (
            <div>
              <div className="drawer-step-label">Selected Template</div>
              <div className="template-preview-card">
                <div>
                  <div className="tmpl-name">{template?.name || 'No template selected'}</div>
                  <div className="tmpl-sub">{template?.category || 'Custom'}</div>
                </div>
              </div>
              <p style={{ color: '#666', fontSize: 13, marginTop: 12 }}>
                This template will be sent as-is. To edit it first, close this drawer and use the editor.
              </p>
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="drawer-step-label">Email Subject Line</div>
              <input
                className="drawer-input"
                placeholder="Enter subject line..."
                value={subject}
                onChange={e => setSubject(e.target.value)}
                autoFocus
              />
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="drawer-step-label">Choose Contact Source</div>

              {/* Manual emails */}
              <div
                className={`contact-source-option ${contactSource?.type === 'manual' ? 'selected' : ''}`}
                onClick={() => setContactSource({ type: 'manual' })}
              >
                <div className="source-label">Enter Emails Manually</div>
                <div className="source-count">Paste or type addresses</div>
              </div>

              {contactSource?.type === 'manual' && (
                <div className="manual-email-input">
                  <textarea
                    className="drawer-textarea"
                    placeholder="Enter email addresses, one per line or comma-separated&#10;e.g. fan@gmail.com, another@email.com"
                    value={manualEmails}
                    onChange={e => setManualEmails(e.target.value)}
                    autoFocus
                    rows={5}
                  />
                  {parsedManual.length > 0 && (
                    <div className="manual-email-count">
                      {parsedManual.length} valid email{parsedManual.length !== 1 ? 's' : ''} detected
                    </div>
                  )}
                </div>
              )}

              {/* CRM Lists */}
              <div
                className={`contact-source-option ${contactSource?.type === 'crm-list' ? 'selected' : ''}`}
                onClick={() => setContactSource({ type: 'crm-list' })}
                style={{ marginTop: 8 }}
              >
                <div className="source-label">CRM Lists</div>
                <div className="source-count">{crmLists.length} lists available</div>
              </div>

              {contactSource?.type === 'crm-list' && (
                <div className="crm-list-picker">
                  {crmLists.length === 0 && <div style={{ color: '#666', fontSize: 13 }}>No CRM lists found</div>}
                  {crmLists.map(list => (
                    <div
                      key={list._id}
                      className={`contact-source-option ${selectedListIds.includes(list._id) ? 'selected' : ''}`}
                      style={{ marginBottom: 6 }}
                      onClick={() => setSelectedListIds(prev =>
                        prev.includes(list._id) ? prev.filter(id => id !== list._id) : [...prev, list._id]
                      )}
                    >
                      <div className="source-label">{list.name}</div>
                      <div className="source-count">{list.count || 0} contacts</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Community Fans */}
              <div
                className={`contact-source-option ${contactSource?.type === 'community-fans' ? 'selected' : ''}`}
                onClick={() => setContactSource({ type: 'community-fans' })}
                style={{ marginTop: 8 }}
              >
                <div className="source-label">Community Fans</div>
                <div className="source-count">{fansCount !== null ? `${fansCount} fans` : 'Loading...'}</div>
              </div>

              {contactSource && (
                <div className="recipient-preview">
                  Sending to <strong>{recipientCount().toLocaleString()}</strong> recipient{recipientCount() !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="drawer-step-label">Review Before Sending</div>
              <div className="review-row">
                <span className="review-label">Template</span>
                <span className="review-value">{template?.name}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Subject</span>
                <span className="review-value">{subject}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Contact Source</span>
                <span className="review-value">
                  {contactSource?.type === 'community-fans' && 'Community Fans'}
                  {contactSource?.type === 'crm-list' && 'CRM Lists'}
                  {contactSource?.type === 'manual' && `${parsedManual.length} manual email${parsedManual.length !== 1 ? 's' : ''}`}
                </span>
              </div>
              <div className="review-row">
                <span className="review-label">Recipients</span>
                <span className="review-value">{recipientCount().toLocaleString()}</span>
              </div>
              {error && <div className="drawer-error">{error}</div>}
            </div>
          )}
        </div>

        <div className="campaign-drawer-footer">
          {step > 0 && (
            <button className="drawer-btn-back" onClick={() => setStep(s => s - 1)}>
              Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button className="drawer-btn-next" onClick={() => setStep(s => s + 1)} disabled={!canProceed()}>
              Next →
            </button>
          ) : (
            <button className="drawer-btn-send" onClick={handleSend} disabled={sending || !canProceed()}>
              {sending ? 'Sending...' : `Send to ${recipientCount().toLocaleString()} →`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
